import IconButton from '@mui/material/IconButton';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell, { TableCellProps } from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TablePagination from '@mui/material/TablePagination';
import TableRow from '@mui/material/TableRow';
import Typography from '@mui/material/Typography';
import { SxProps, Theme } from '@mui/material/styles';
import { ReactNode, useState } from 'react';
import { useScreen } from '../../hooks';
import { ChevronLeftIcon, ChevronRightIcon } from '../../icons';
import { FlexBox, FlexBoxProps } from '../layout';

export type Column<T> = {
  id: string;
  label: string | ReactNode;
  cellValue?: (row: T) => string | ReactNode;
  cellProps?: TableCellProps;
};

interface ResponsiveTableProps<T> {
  columns: Column<T>[];
  rows: T[];
  noDataMessage?: string;
  rowsPerPageOptions?: number[];
  initialRowsPerPage?: number;
  pagination?: boolean;
  enableMobileView?: boolean;
  isMobile?: boolean;
  prevIcon?: ReactNode;
  nextIcon?: ReactNode;
  containerProps?: FlexBoxProps;
  rowSx?: (row: T) => SxProps<Theme> | undefined;
}

const ResponsiveTable = <T extends Record<string, string | number | boolean>>({
  columns,
  rows,
  noDataMessage = 'No data available',
  rowsPerPageOptions = [25, 50, 100],
  initialRowsPerPage = 25,
  pagination = false,
  enableMobileView = false,
  isMobile,
  prevIcon,
  nextIcon,
  containerProps,
  rowSx,
}: ResponsiveTableProps<T>) => {
  const { isMobile: screenIsMobile } = useScreen();
  const resolvedIsMobile = isMobile ?? screenIsMobile;

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(initialRowsPerPage);

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const paginatedRows = pagination
    ? rows.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
    : rows;

  const showMobileView = enableMobileView && resolvedIsMobile;

  if (showMobileView) {
    return (
      <FlexBox
        flexDirection={'column'}
        alignItems={'center'}
        gap={2}
        {...containerProps}
      >
        <List>
          {paginatedRows.length === 0 ? (
            <Typography align="center">{noDataMessage}</Typography>
          ) : (
            paginatedRows.map((row, rowIndex) => (
              <ListItem
                key={rowIndex}
                divider
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'flex-start',
                  width: '350px',
                  py: 2,
                }}
              >
                <FlexBox flexDirection="column" flex="1" gap={1}>
                  {columns.map((column) => (
                    <FlexBox pl={1} key={column.id} flexDirection={'column'}>
                      <Typography variant="subtitle2">
                        {column.label}:
                      </Typography>
                      <Typography variant="body2">
                        {column.cellValue
                          ? column.cellValue(row)
                          : row[column.id]}
                      </Typography>
                    </FlexBox>
                  ))}
                </FlexBox>
              </ListItem>
            ))
          )}
        </List>
        {pagination && (
          <FlexBox justifyContent="space-between" alignItems="center" gap={2}>
            <IconButton
              onClick={() => handleChangePage(null, page - 1)}
              disabled={page === 0}
              size="small"
            >
              {prevIcon ?? <ChevronLeftIcon />}
            </IconButton>
            <Typography variant="caption">
              Page {page + 1} of {Math.ceil(rows.length / rowsPerPage)}
            </Typography>
            <IconButton
              onClick={() => handleChangePage(null, page + 1)}
              disabled={page >= Math.ceil(rows.length / rowsPerPage) - 1}
              size="small"
            >
              {nextIcon ?? <ChevronRightIcon />}
            </IconButton>
          </FlexBox>
        )}
      </FlexBox>
    );
  }

  return (
    <FlexBox overflow={'auto'} {...containerProps}>
      <Table>
        <TableHead>
          <TableRow>
            {columns.map((column) => (
              <TableCell key={column.id}>{column.label}</TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {paginatedRows.length === 0 ? (
            <TableRow>
              <TableCell colSpan={columns.length} align="center">
                {noDataMessage}
              </TableCell>
            </TableRow>
          ) : (
            paginatedRows.map((row, rowIndex) => (
              <TableRow key={rowIndex} sx={rowSx?.(row)}>
                {columns.map((column) => (
                  <TableCell key={column.id} {...column.cellProps}>
                    {column.cellValue ? column.cellValue(row) : row[column.id]}
                  </TableCell>
                ))}
              </TableRow>
            ))
          )}
        </TableBody>
      </Table>
      {pagination && (
        <TablePagination
          rowsPerPageOptions={rowsPerPageOptions}
          component="div"
          count={rows.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />
      )}
    </FlexBox>
  );
};

export default ResponsiveTable;
